import moment from 'moment';

import {
  IServiceAuditorFindingFeedback,
  IServiceAuditorFindingFeedbackInput,
  ServiceAuditorFindingVerdict,
} from '@commenda/api/agent-platform/service-auditor/types';

import { IServiceAuditBadgeConfig } from './ServiceAuditFindingBadgeHelper';

export interface IServiceAuditFeedbackValues {
  verdict: ServiceAuditorFindingVerdict | null;
  reason: string;
}

export interface IServiceAuditFeedbackErrors {
  verdict?: string;
  reason?: string;
}

export interface IServiceAuditFeedbackValidation {
  errors: IServiceAuditFeedbackErrors;
  // Set only when every field is valid; the reason is trimmed.
  input: IServiceAuditorFindingFeedbackInput | null;
}

export interface IServiceAuditFeedbackOption<T extends string> {
  value: T;
  label: string;
}

export interface IServiceAuditNotificationCopy {
  title: string;
  message: string;
}

export class ServiceAuditFeedbackHelper {
  static readonly REASON_MAX_CHARACTERS = 2000;

  static readonly BLOCK_LABEL = 'Your feedback';

  static readonly VERDICT_QUESTION = 'Is this finding real?';

  static readonly REASON_LABEL = 'Reason';

  static readonly REASON_PLACEHOLDER =
    'Say why. Name the form, field or rule where you can.';

  static readonly SAVE_LABEL = 'Save feedback';

  static readonly SAVING_LABEL = 'Saving...';

  static readonly EDIT_LABEL = 'Edit feedback';

  static readonly CANCEL_EDIT_LABEL = 'Cancel';

  static readonly UNNAMED_REVIEWER = 'a reviewer';

  static readonly FEEDBACK_CLOSED_TEXT =
    'No feedback given. Only the latest audit takes feedback.';

  static readonly ERRORS = {
    verdictMissing: 'Choose real or not real.',
    reasonMissing: 'Add a reason.',
    reasonTooLong: 'Keep the reason under 2,000 characters.',
  };

  static readonly SAVED_TOAST: IServiceAuditNotificationCopy = {
    title: 'Feedback saved',
    message: "Thanks. This finding now counts toward the auditor's accuracy.",
  };

  static readonly SAVE_FAILED_TOAST: IServiceAuditNotificationCopy = {
    title: 'Could not save feedback',
    message: 'Your feedback was not saved. Try again.',
  };

  static readonly VERDICT_OPTIONS: ReadonlyArray<
    IServiceAuditFeedbackOption<ServiceAuditorFindingVerdict>
  > = [
    { value: ServiceAuditorFindingVerdict.REAL, label: 'Real' },
    { value: ServiceAuditorFindingVerdict.NOT_REAL, label: 'Not real' },
  ];

  static readonly VERDICT_BADGES: Readonly<
    Record<ServiceAuditorFindingVerdict, IServiceAuditBadgeConfig>
  > = {
    [ServiceAuditorFindingVerdict.REAL]: { label: 'Real', variant: 'success' },
    [ServiceAuditorFindingVerdict.NOT_REAL]: {
      label: 'Not real',
      variant: 'gray',
    },
  };

  // Saved values when editing; an empty form otherwise. A previous round's verdict never pre-fills the form.
  static getInitialValues(
    feedback: Readonly<IServiceAuditorFindingFeedback> | null
  ): IServiceAuditFeedbackValues {
    return {
      verdict: feedback?.verdict ?? null,
      reason: feedback?.reason ?? '',
    };
  }

  static validateFeedback(
    values: Readonly<IServiceAuditFeedbackValues>
  ): IServiceAuditFeedbackValidation {
    const reason = values.reason.trim();
    const errors: IServiceAuditFeedbackErrors = {};
    if (!values.verdict) {
      errors.verdict = ServiceAuditFeedbackHelper.ERRORS.verdictMissing;
    }
    if (!reason) {
      errors.reason = ServiceAuditFeedbackHelper.ERRORS.reasonMissing;
    } else if (
      reason.length > ServiceAuditFeedbackHelper.REASON_MAX_CHARACTERS
    ) {
      errors.reason = ServiceAuditFeedbackHelper.ERRORS.reasonTooLong;
    }
    const { verdict } = values;
    const input =
      verdict && Object.keys(errors).length === 0 ? { verdict, reason } : null;
    return { errors, input };
  }

  static findVerdict(value: string): ServiceAuditorFindingVerdict | null {
    return (
      ServiceAuditFeedbackHelper.VERDICT_OPTIONS.find(
        (option) => option.value === value
      )?.value ?? null
    );
  }

  static getVerdictBadge(
    verdict: ServiceAuditorFindingVerdict
  ): IServiceAuditBadgeConfig {
    return ServiceAuditFeedbackHelper.VERDICT_BADGES[verdict];
  }

  static getSavedMeta(
    feedback: Readonly<IServiceAuditorFindingFeedback>
  ): string {
    const savedBy = `Saved by ${ServiceAuditFeedbackHelper.getReviewerName(feedback)}`;
    const savedOn = ServiceAuditFeedbackHelper.formatFeedbackDate(
      feedback.reviewedAt
    );
    return savedOn ? `${savedBy} on ${savedOn}` : savedBy;
  }

  private static getReviewerName(
    feedback: Readonly<IServiceAuditorFindingFeedback>
  ): string {
    return (
      feedback.reviewedByName?.trim() ||
      ServiceAuditFeedbackHelper.UNNAMED_REVIEWER
    );
  }

  private static formatFeedbackDate(date: string | null): string {
    return date ? moment(date).format('DD MMM, YYYY') : '';
  }
}
